import React, { useState, useEffect, useCallback} from 'react';
import { Loader2, Star, Award, Trash2} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '../../lib/utils';
import { API, CUSTOM_TYPE_COLORS, BUILTIN_CATEGORIES} from './docTypesShared';

export const CustomDocumentTypesAdminPanel = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [rubriques, setRubriques] = useState([]);
  const [eligibilityOptions, setEligibilityOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form: nouveau type
  const [newLabel, setNewLabel] = useState('');
  const [newCategory, setNewCategory] = useState('Personnalisé');
  const [newColor, setNewColor] = useState('purple');
  const [newEligibility, setNewEligibility] = useState('');
  const [creating, setCreating] = useState(false);

  // Form: nouvelle rubrique
  const [newRubriqueLabel, setNewRubriqueLabel] = useState('');
  const [newRubriqueColor, setNewRubriqueColor] = useState('amber');
  const [creatingRubrique, setCreatingRubrique] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [tRes, rRes, eRes] = await Promise.all([
        axios.get(`${API}/api/admin/ged/document-types`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/admin/ged/document-rubriques`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/admin/ged/eligibility-rubriques`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setTypes(tRes.data || []);
      setRubriques(rRes.data || []);
      setEligibilityOptions(eRes.data || []);
    } catch (err) {
      toast.error('Impossible de charger les types/rubriques personnalisés');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const handleCreate = async () => {
    if (!newLabel || newLabel.trim().length < 2) {
      toast.error('Saisissez un libellé d\'au moins 2 caractères');
      return;
    }
    setCreating(true);
    try {
      await axios.post(
        `${API}/api/admin/ged/document-types`,
        {
          label: newLabel.trim(),
          category: (newCategory || 'Personnalisé').trim() || 'Personnalisé',
          color_keyword: newColor,
          eligibility_rubrique: newEligibility || null},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Type "${newLabel}" ajouté`);
      setNewLabel('');
      setNewColor('purple');
      setNewEligibility('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (typeId, label) => {
    if (!window.confirm(`Supprimer le type "${label}" ?\n\nLes documents existants conservent ce type, mais il ne sera plus proposé à la création.`)) return;
    try {
      await axios.delete(`${API}/api/admin/ged/document-types/${typeId}`, {
        headers: { Authorization: `Bearer ${token}` }});
      toast.success(`Type "${label}" supprimé`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleCreateRubrique = async () => {
    if (!newRubriqueLabel || newRubriqueLabel.trim().length < 2) {
      toast.error('Saisissez un libellé de rubrique d\'au moins 2 caractères');
      return;
    }
    setCreatingRubrique(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/ged/document-rubriques`,
        { label: newRubriqueLabel.trim(), color_keyword: newRubriqueColor },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Rubrique "${newRubriqueLabel}" ajoutée`);
      // Auto-sélectionne la rubrique fraîchement créée dans le formulaire type
      setNewCategory(res.data?.label || newRubriqueLabel.trim());
      setNewRubriqueLabel('');
      setNewRubriqueColor('amber');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreatingRubrique(false);
    }
  };

  const handleDeleteRubrique = async (rubriqueId, label) => {
    if (!window.confirm(`Supprimer la rubrique "${label}" ?\n\nLes types qui y sont rattachés conservent simplement ce nom.`)) return;
    try {
      await axios.delete(`${API}/api/admin/ged/document-rubriques/${rubriqueId}`, {
        headers: { Authorization: `Bearer ${token}` }});
      toast.success(`Rubrique "${label}" supprimée`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Catégories proposées dans le Select (natives + rubriques perso, dédoublonnées)
  const categoryOptions = React.useMemo(() => {
    const set = new Map();
    BUILTIN_CATEGORIES.forEach(c => set.set(c, { label: c, custom: false }));
    rubriques.forEach(r => set.set(r.label, { label: r.label, custom: true }));
    return Array.from(set.values());
  }, [rubriques]);

  return (
    <div className="mt-3 rounded-lg border border-purple-200 bg-purple-50/50" data-testid="custom-doc-types-panel">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-purple-900 hover:bg-purple-100/50 transition rounded-lg"
        data-testid="toggle-custom-types-panel"
      >
        <span className="flex items-center gap-2">
          <Star className="h-3.5 w-3.5" />
          Gérer les types & rubriques de documents personnalisés (Super Admin)
        </span>
        <span className="text-purple-600">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="p-3 border-t border-purple-200 space-y-4">
          {/* ============ RUBRIQUES PERSONNALISÉES ============ */}
          <div className="space-y-2 bg-white rounded p-3 border border-amber-200">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-amber-700 font-semibold">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
              Nouvelle rubrique (catégorie)
            </div>
            <p className="text-[11px] text-gray-500 leading-snug">
              Crée une catégorie regroupant des types de documents (ex. « Titre Professionnel »).
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="Ex: Titre Professionnel"
                value={newRubriqueLabel}
                onChange={(e) => setNewRubriqueLabel(e.target.value)}
                className="h-8 text-sm flex-1"
                data-testid="new-rubrique-label-input"
              />
              <select
                value={newRubriqueColor}
                onChange={(e) => setNewRubriqueColor(e.target.value)}
                className="h-8 text-sm border border-gray-300 rounded px-2 bg-white"
                data-testid="new-rubrique-color-select"
              >
                {CUSTOM_TYPE_COLORS.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <Button
              onClick={handleCreateRubrique}
              disabled={creatingRubrique || !newRubriqueLabel.trim()}
              size="sm"
              className="w-full h-8 bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="create-rubrique-btn"
            >
              {creatingRubrique ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Star className="h-3 w-3 mr-1" />}
              Ajouter cette rubrique
            </Button>

            {rubriques.length > 0 && (
              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1">
                  Rubriques existantes ({rubriques.length})
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-[110px] overflow-y-auto">
                  {rubriques.map(r => (
                    <div
                      key={r.id}
                      className={cn("inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px]", r.color)}
                      data-testid={`rubrique-chip-${r.id}`}
                    >
                      <span className="font-medium">{r.label}</span>
                      <button
                        type="button"
                        onClick={() => handleDeleteRubrique(r.id, r.label)}
                        className="ml-1 text-red-500 hover:text-red-700"
                        title="Supprimer la rubrique"
                        aria-label="Supprimer cette rubrique"
                        data-testid={`delete-rubrique-${r.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ============ TYPE DE DOCUMENT ============ */}
          <div className="space-y-2 bg-white rounded p-3 border border-purple-100">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-purple-700 font-semibold">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-500" />
              Nouveau type de document
            </div>
            <Input
              placeholder="Ex: Attestation de TVA"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              className="h-8 text-sm"
              data-testid="new-type-label-input"
            />
            <div className="flex gap-2">
              <select
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="h-8 text-sm border border-gray-300 rounded px-2 bg-white flex-1 truncate"
                data-testid="new-type-category-select"
              >
                {categoryOptions.map(opt => (
                  <option key={opt.label} value={opt.label}>
                    {opt.custom ? '★ ' : ''}{opt.label}
                  </option>
                ))}
              </select>
              <select
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                className="h-8 text-sm border border-gray-300 rounded px-2 bg-white"
                data-testid="new-type-color-select"
              >
                {CUSTOM_TYPE_COLORS.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Rubrique d'éligibilité BATICO (optionnelle) — connecte le type au simulateur */}
            <div className="space-y-1 pt-1 border-t border-purple-100">
              <Label className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold flex items-center gap-1.5">
                <Award className="h-3 w-3" />
                Rubrique d'éligibilité BATICO
                <span className="text-gray-400 normal-case font-normal">(optionnel — connecte au simulateur)</span>
              </Label>
              <select
                value={newEligibility}
                onChange={(e) => setNewEligibility(e.target.value)}
                className="h-8 text-sm border border-indigo-200 rounded px-2 bg-indigo-50/40 w-full"
                data-testid="new-type-eligibility-select"
              >
                <option value="">— Aucune (non détecté par le simulateur)</option>
                {(() => {
                  // Group by `group` field for visual grouping
                  const grouped = (eligibilityOptions || []).reduce((acc, opt) => {
                    const g = opt.group || 'Autre';
                    if (!acc[g]) acc[g] = [];
                    acc[g].push(opt);
                    return acc;
                  }, {});
                  return Object.entries(grouped).map(([g, opts]) => (
                    <optgroup key={g} label={g}>
                      {opts.map(o => (
                        <option key={o.key} value={o.key}>{o.label}</option>
                      ))}
                    </optgroup>
                  ));
                })()}
              </select>
              {newEligibility && (
                <p className="text-[10px] text-indigo-600 italic">
                  ✓ Tout document de ce type uploadé pour un membre sera reconnu automatiquement comme justificatif d'éligibilité par le simulateur.
                </p>
              )}
            </div>

            <Button
              onClick={handleCreate}
              disabled={creating || !newLabel.trim()}
              size="sm"
              className="w-full h-8 bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="create-custom-type-btn"
            >
              {creating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Star className="h-3 w-3 mr-1" />}
              Ajouter ce type
            </Button>
          </div>

          {/* ============ LISTE DES TYPES ============ */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-purple-700 font-semibold mb-1">
              Types existants ({types.length})
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-purple-500" /></div>
            ) : types.length === 0 ? (
              <div className="text-xs text-gray-500 italic py-2 text-center">Aucun type personnalisé</div>
            ) : (
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {types.map(t => {
                  // Résoudre le label de la rubrique d'éligibilité (si présente)
                  const eligibilityLabel = (() => {
                    if (!t.eligibility_rubrique) return null;
                    const opt = (eligibilityOptions || []).find(o => o.key === t.eligibility_rubrique);
                    return opt ? opt.label : t.eligibility_rubrique;
                  })();
                  return (
                    <div key={t.id} className="flex items-center justify-between gap-2 bg-white px-2 py-1.5 rounded border border-purple-100" data-testid={`custom-type-row-${t.id}`}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Badge variant="outline" className={cn("text-[10px] shrink-0", t.color)}>
                          {t.category}
                        </Badge>
                        <span className="text-xs text-gray-800 truncate">{t.label}</span>
                        {eligibilityLabel && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold text-indigo-700 bg-indigo-100 border border-indigo-200 shrink-0"
                            title={`Lié au simulateur : ${eligibilityLabel}`}
                            data-testid={`custom-type-eligibility-${t.id}`}
                          >
                            <Award className="h-2.5 w-2.5" />
                            {eligibilityLabel}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDelete(t.id, t.label)}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                        title="Supprimer"
                        aria-label="Supprimer ce type"
                        data-testid={`delete-custom-type-${t.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// AuditLogPanel — Journal d'activité RGPD (super_admin / admin)
// Panneau pliable affichant qui a fait quoi et quand sur les documents du
// membre (consultation, téléchargement, ZIP, suppression).
// ============================================================================
